import { useState } from "react";
import { Download } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { SyncBadge, ModeBadge } from "@/components/common/SyncBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dateOnly, dt, duration, money, pct, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(null);
  const [limits, setLimits] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => get(`/campaigns/${id}`),
    refetchInterval: 6000,
  });
  const { data: review } = useQuery({
    queryKey: ["deployment-review", id],
    queryFn: () => get(`/campaigns/${id}/deployment-review`),
    refetchInterval: 15000,
  });
  const { data: members } = useQuery({
    queryKey: ["campaign-contacts", id],
    queryFn: () => get(`/campaigns/${id}/contacts`, { page_size: 25 }),
  });
  const { data: numbers } = useQuery({
    queryKey: ["numbers"],
    queryFn: () => get("/phone-numbers"),
  });

  const action = useMutation({
    mutationFn: ({ verb, reason }) => api.post(`/campaigns/${id}/${verb}`, { confirm: true, reason }),
    onSuccess: (_, v) => {
      toast.success(`Campaign ${v.verb} applied`);
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-contacts", id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const saveLimits = useMutation({
    mutationFn: () =>
      api.put(`/campaigns/${id}`, {
        name: data.name, product: data.product, description: data.description,
        country: data.country, language: data.language, timezone: data.timezone,
        agent_id: data.agent_id, agent_version: data.agent_version,
        voice_provider: data.voice_provider, telephony_provider: data.telephony_provider,
        phone_number: phoneNumber || data.phone_number, limits: { ...data.limits, ...limits },
        schedule: data.schedule, budget: data.budget,
      }),
    onSuccess: () => {
      toast.success("Campaign configuration updated");
      setLimits(null);
      setPhoneNumber(null);
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/campaigns/${id}`),
    onSuccess: () => {
      toast.success("Campaign deleted");
      navigate("/campaigns");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const testCall = useMutation({
    mutationFn: () => api.post(`/campaigns/${id}/test-call`, { mode: "browser" }),
    onSuccess: () => toast.success("Test call placed through the live provider"),
    onError: (e) => toast.error(apiError(e)),
  });

  const exportToday = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/campaigns/${id}/today.xlsx`, { responseType: "blob" });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const header = response.headers?.["content-disposition"] || "";
      const named = /filename="?([^"]+)"?/.exec(header);
      link.href = url;
      link.download = named?.[1] || `${data.name}-today.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(apiError(error, "Could not export today's statistics."));
    } finally {
      setExporting(false);
    }
  };

  if (isLoading || !data) return <p className="text-sm text-slate-500">Loading campaign…</p>;

  const running = data.status === "running";
  const chosenNumber = phoneNumber || data.phone_number || "";
  const numberDirty = Boolean(phoneNumber && phoneNumber !== data.phone_number);
  const numberOptions = (numbers || []).filter((n) => (n.status || "active") === "active" || n.phone === data.phone_number);
  if (data.phone_number && !numberOptions.some((n) => n.phone === data.phone_number)) {
    numberOptions.unshift({ phone: data.phone_number, country: data.country, languages: [data.language].filter(Boolean) });
  }

  return (
    <>
      <PageHeader
        testId="campaign-detail-page"
        title={data.name}
        subtitle={`${data.product} · ${data.country} · ${(data.language || "").toUpperCase()} · agent ${data.agent_name} v${data.agent_version}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <ModeBadge mode={data.provider_mode || "live"} testId="campaign-mode-badge" />
            <SyncBadge status={review?.agent?.sync_status || "not_connected"} testId="campaign-sync-badge" />
            <StatusBadge value={data.status} testId="campaign-detail-status" />
            <Button variant="outline" size="sm" onClick={() => setReviewOpen(true)} data-testid="detail-review">
              Deployment review
            </Button>
            {can("campaigns", "write") ? (
              <>
                <Button variant="outline" size="sm" onClick={() => testCall.mutate()} data-testid="detail-test-call">
                  Test call
                </Button>
                {["draft", "scheduled", "paused"].includes(data.status) ? (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={review && !review.can_launch}
                    title={review && !review.can_launch ? `Blocked: ${review.blockers.join("; ")}` : ""}
                    onClick={() => setConfirm({ verb: "start", title: `Start “${data.name}”?`, description: `Mode: ${(data.provider_mode || "live").toUpperCase()}. Estimated cost ${review ? `${review.estimated_min_cost}–${review.estimated_max_cost} USD` : "unknown"}. Calls begin according to schedule, concurrency and budget limits.` })}
                    data-testid="detail-start"
                  >
                    Start
                  </Button>
                ) : null}
                {running ? (
                  <Button size="sm" variant="outline" onClick={() => action.mutate({ verb: "pause" })} data-testid="detail-pause">
                    Pause
                  </Button>
                ) : null}
                {data.status === "paused" ? (
                  <Button size="sm" variant="outline" onClick={() => action.mutate({ verb: "resume" })} data-testid="detail-resume">
                    Resume
                  </Button>
                ) : null}
                {["completed", "stopped", "failed", "archived"].includes(data.status) ? (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => setConfirm({
                      verb: "restart",
                      title: `Restart “${data.name}”?`,
                      description: "The same campaign starts again. Contacts that were already called go back in the queue. Do-not-call numbers stay blocked, and the previous call history is kept. If the budget is already used up, raise it first.",
                    })}
                    data-testid="detail-restart"
                  >
                    Restart
                  </Button>
                ) : null}
                {isAdmin ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                    onClick={() => setConfirm({
                      verb: "delete",
                      title: `Delete “${data.name}”?`,
                      description: data.status === "running"
                        ? "Stop the campaign first. A running campaign cannot be deleted."
                        : "The campaign leaves the list. Contacts and call history stay. Queued calls for it are cancelled.",
                    })}
                    data-testid="detail-delete"
                  >
                    Delete
                  </Button>
                ) : null}
                {["running", "paused", "scheduled"].includes(data.status) ? (
                  <Button
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700"
                    onClick={() => setConfirm({ verb: "stop", title: `Stop “${data.name}”?`, description: "Queued calls are cancelled and active calls end." })}
                    data-testid="detail-stop"
                  >
                    Stop
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        }
      />

      {data.waiting_reason ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="campaign-waiting-reason">
          {data.waiting_reason}
        </div>
      ) : data.paused_reason ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="campaign-pause-reason">
          Paused: {data.paused_reason}
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">All calls</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
          <KpiTile testId="cd-contacts" label="Contacts" value={data.contact_count} />
          <KpiTile testId="cd-calls" label="Calls" value={data.calls_made} />
          <KpiTile
            testId="cd-answered"
            label="Answered"
            value={data.answered_count ?? 0}
            hint={`${pct(data.answer_rate)} · ${data.conversations ?? 0} conversations`}
            tone="success"
          />
          <KpiTile
            testId="cd-voicemail"
            label="Voicemails"
            value={data.voicemail_count ?? 0}
            hint={pct(data.voicemail_rate)}
            tone="warning"
          />
          <KpiTile testId="cd-interested" label="Interested" value={data.interested_count} tone="success" />
          <KpiTile testId="cd-callbacks" label="Callbacks" value={data.callback_count ?? 0} />
          <KpiTile testId="cd-leads" label="Leads" value={data.lead_count ?? 0} tone="success" />
          <KpiTile testId="cd-cost" label="Spend" value={money(data.cost)} hint={`${duration(data.avg_duration)} avg`} />
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Today · Europe/Tirane</p>
          <Button
            variant="outline"
            size="sm"
            data-testid="campaign-today-export"
            disabled={exporting}
            onClick={exportToday}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {exporting ? "Exporting…" : "Export Excel"}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
          <KpiTile testId="cd-today-calls" label="Calls" value={data.today?.calls ?? 0} />
          <KpiTile
            testId="cd-today-answered"
            label="Answered"
            value={data.today?.answered ?? 0}
            hint={pct(data.today?.answer_rate)}
            tone="success"
          />
          <KpiTile testId="cd-today-voicemail" label="Voicemails" value={data.today?.voicemail ?? 0} tone="warning" />
          <KpiTile testId="cd-today-interested" label="Interested" value={data.today?.interested ?? 0} tone="success" />
          <KpiTile testId="cd-today-callbacks" label="Callbacks" value={data.today?.callbacks ?? 0} />
          <KpiTile testId="cd-today-leads" label="Leads" value={data.today?.leads ?? 0} />
          <KpiTile testId="cd-today-spend" label="Spend" value={money(data.today?.spend)} />
        </div>
      </div>

      {data.brief_pickups ? (
        <p className="text-sm text-slate-500" data-testid="cd-brief-note">
          {Number(data.brief_pickups).toLocaleString()} answered calls were brief pickups that ended as “customer did not answer”.
        </p>
      ) : null}

      <div className="panel px-4 py-3">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">Progress</span>
          <span className="font-mono text-slate-500">{data.progress}%</span>
        </div>
        <Progress value={data.progress} className="mt-2 h-2" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="campaign-tabs" className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="calls" data-testid="tab-calls">Recent calls</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions">Versions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title="Outcome breakdown">
            <DataTable
              testId="campaign-outcomes-table"
              columns={[
                { key: "outcome", label: "Outcome", render: (r) => <StatusBadge value={r._id} /> },
                { key: "count", label: "Calls" },
              ]}
              rows={(data.outcome_breakdown || []).map((o, i) => ({ ...o, id: o._id || i }))}
              empty={{ title: "No completed calls yet" }}
            />
          </Section>
          <Section title="Schedule and budget">
            <dl className="grid grid-cols-2 gap-3 p-4 text-sm">
              {[
                ["Start date", dateOnly(data.schedule?.start_date)],
                ["End date", dateOnly(data.schedule?.end_date)],
                ["Calling hours", `${data.schedule?.start_hour} – ${data.schedule?.end_hour}`],
                ["Retries", `${data.schedule?.max_retries} max`],
                ["Daily budget", money(data.budget?.daily_budget)],
                ["Total budget", money(data.budget?.total_budget)],
                ["Max cost per call", money(data.budget?.max_cost_per_call)],
                ["Auto pause", data.budget?.auto_pause_on_budget ? "Enabled" : "Disabled"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="kpi-label">{k}</dt>
                  <dd className="font-medium text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
          </Section>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <Section title={`Campaign contacts (${members?.total ?? 0})`}>
            <DataTable
              testId="campaign-members-table"
              columns={[
                { key: "name", label: "Contact", render: (r) => `${r.contact?.first_name || ""} ${r.contact?.last_name || ""}`.trim() || "—" },
                { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs">{r.contact?.phone}</span> },
                { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                { key: "attempts", label: "Attempts" },
                { key: "last_outcome", label: "Last outcome", render: (r) => (r.last_outcome ? <StatusBadge value={r.last_outcome} /> : "—") },
              ]}
              rows={members?.items || []}
              empty={{ title: "No contacts assigned" }}
            />
          </Section>
        </TabsContent>

        <TabsContent value="calls" className="mt-4">
          <Section title="Recent calls">
            <DataTable
              testId="campaign-calls-table"
              onRowClick={(r) => navigate(`/calls/${r.id}`)}
              columns={[
                { key: "contact_name", label: "Contact" },
                { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
                { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                { key: "outcome", label: "Outcome", render: (r) => (r.outcome ? <StatusBadge value={r.outcome} /> : "—") },
                { key: "interest_score", label: "Score", render: (r) => <ScoreBadge score={r.interest_score} /> },
                { key: "duration_seconds", label: "Duration", render: (r) => duration(r.duration_seconds) },
                { key: "total_cost", label: "Cost", render: (r) => money(r.total_cost) },
                { key: "started_at", label: "Started", render: (r) => dt(r.started_at) },
              ]}
              rows={data.recent_calls || []}
              empty={{ title: "No calls yet" }}
            />
          </Section>
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          <Section title="Configuration" subtitle={running ? "Agent, number and language are locked while the campaign runs" : "Editable while the campaign is not running"}>
            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
              {[
                ["AI agent", `${data.agent_name} v${data.agent_version}`],
                ["Language", (data.language || "").toUpperCase()],
                ["Voice provider", title(data.voice_provider)],
                ["Telephony provider", title(data.telephony_provider)],
                ["Config version", `v${data.config_version}`],
              ].map(([k, v]) => (
                <div key={k} className="rounded-md border border-slate-200 px-3 py-2">
                  <p className="kpi-label">{k}</p>
                  <p className="text-sm font-medium text-slate-800">{v}</p>
                </div>
              ))}
              <div className="space-y-1.5 md:col-span-2">
                <Label>Outbound number</Label>
                <Select value={chosenNumber || undefined} disabled={running} onValueChange={setPhoneNumber}>
                  <SelectTrigger data-testid="config-phone-number"><SelectValue placeholder="Choose a caller ID" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {numberOptions.map((n) => (
                      <SelectItem key={n.id || n.phone} value={n.phone}>
                        {n.phone} · {(n.country || "").toUpperCase()} · {((n.languages || []).join(", ") || "any").toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {running
                    ? "Pause the campaign to choose a different caller ID."
                    : "Pick the number for this campaign. Use one number for Italian calls and another for English."}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Max concurrent calls</Label>
                <Input
                  data-testid="config-max-concurrent"
                  type="number"
                  value={(limits?.max_concurrent_calls ?? data.limits?.max_concurrent_calls) || 1}
                  onChange={(e) => setLimits((l) => ({ ...l, max_concurrent_calls: Number(e.target.value) }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Calls per minute</Label>
                <Input
                  data-testid="config-calls-per-minute"
                  type="number"
                  value={(limits?.calls_per_minute ?? data.limits?.calls_per_minute) || 1}
                  onChange={(e) => setLimits((l) => ({ ...l, calls_per_minute: Number(e.target.value) }))}
                />
              </div>
              <div className="flex items-end">
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={(!limits && !numberDirty) || saveLimits.isPending || running}
                  onClick={() => saveLimits.mutate()}
                  data-testid="config-save"
                >
                  Save
                </Button>
              </div>
            </div>
            {running ? (
              <p className="border-t border-slate-200 px-4 py-2.5 text-sm text-amber-700">
                To change the agent version, phone number or core prompt, pause the campaign first — a new
                configuration version will be created.
              </p>
            ) : null}
          </Section>
        </TabsContent>

        <TabsContent value="versions" className="mt-4">
          <Section title="Configuration versions">
            <DataTable
              testId="campaign-versions-table"
              columns={[
                { key: "version", label: "Version", render: (r) => `v${r.version}` },
                { key: "change_notes", label: "Notes" },
                { key: "created_at", label: "Created", render: (r) => dt(r.created_at) },
              ]}
              rows={data.versions || []}
              empty={{ title: "No versions recorded" }}
            />
          </Section>
        </TabsContent>
      </Tabs>

      <div>
        <Link to="/campaigns" className="text-sm text-blue-600 hover:underline" data-testid="back-to-campaigns">
          ← Back to campaigns
        </Link>
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader><DialogTitle>Deployment review · {(review?.mode || "live").toUpperCase()}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm" data-testid="deployment-review">
            <div className="grid grid-cols-2 gap-2">
              {[
                ["Agent", `${review?.agent?.name || "—"} v${review?.agent?.version || "—"}`],
                ["Agent sync", (review?.agent?.sync_status || "not_connected").toUpperCase()],
                ["Phone number", `${review?.phone_number?.phone || "—"} (${review?.phone_number?.sync_status || "not_connected"})`],
                ["Contacts pending", review?.contacts?.pending ?? 0],
                ["Blocked by DNC", review?.contacts?.blocked_dnc ?? 0],
                ["Invalid contacts", review?.contacts?.invalid ?? 0],
                ["Recording", review?.recording_enabled ? "Enabled" : "Disabled"],
                ["Concurrency", `${review?.limits?.max_concurrent_calls || "—"} concurrent`],
                ["Retries", `${review?.schedule?.max_retries ?? "—"} max`],
                ["Budget", `${review?.budget?.daily_budget || 0} daily / ${review?.budget?.total_budget || 0} total`],
                ["Estimated cost", `${review?.estimated_min_cost ?? 0} – ${review?.estimated_max_cost ?? 0} USD`],
                ["DNC validation", review?.dnc_checked ? "Completed" : "Not run"],
              ].map(([k, v]) => (
                <div key={k} className="rounded-md border border-slate-200 px-2.5 py-1.5">
                  <p className="kpi-label">{k}</p>
                  <p className="font-medium text-slate-800">{v}</p>
                </div>
              ))}
            </div>
            {Object.entries(review?.provider_health || {}).map(([provider, health]) => (
              <p key={provider} className="text-xs text-slate-600">
                {provider}: {health.connected ? "connected" : "not connected"} · health {health.status || "unknown"}
                {health.balance != null ? ` · balance ${health.balance} ${health.currency || ""}` : ""}
              </p>
            ))}
            {(review?.warnings || []).map((w) => (
              <p key={w} className="rounded-md bg-amber-50 px-3 py-1.5 text-amber-800">{w}</p>
            ))}
            {(review?.blockers || []).length ? (
              <ul className="space-y-1 rounded-md bg-rose-50 px-3 py-2" data-testid="review-blockers">
                {review.blockers.map((b) => <li key={b} className="text-rose-700">• {b}</li>)}
              </ul>
            ) : (
              <p className="rounded-md bg-emerald-50 px-3 py-2 text-emerald-700">
                All pre-launch checks passed. The campaign can be launched.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm?.title || ""}
        description={confirm?.description || ""}
        destructive={confirm?.verb === "stop" || confirm?.verb === "delete"}
        confirmLabel={title(confirm?.verb || "")}
        onConfirm={async () => {
          if (confirm.verb === "delete") await remove.mutateAsync();
          else await action.mutateAsync({ verb: confirm.verb });
          setConfirm(null);
        }}
        testId="campaign-detail-confirm"
      />
    </>
  );
}